/**
 * Authentication utilities for hybrid localStorage + HttpOnly cookie approach
 * 
 * SIMPLIFIED ARCHITECTURE:
 * - Primary: localStorage (works cross-origin, reliable on mobile)
 * - Secondary: HttpOnly cookie (additional security when same-origin)
 * - Removed: Session fingerprinting (caused mobile issues)
 */

import { authLogger } from '@/utils/authLogger';

export interface CookieValidationResult {
  isValid: boolean;
  userEmail: string | null;
  userName: string | null;
  paymentStatus: string | null;
  expiresAt: Date | null;
  usesRemaining: number | null;
  failureReason: string | null;
  isAdmin?: boolean;
  profileId?: string | null;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/**
 * Get localStorage token for cross-origin fallback
 */
export const getLocalStorageToken = (context?: 'admin' | 'user'): string | null => {
  try {
    if (context === 'admin') {
      return localStorage.getItem('admin_auth_token');
    } else if (context === 'user') {
      return localStorage.getItem('user_auth_token');
    }
    // Try admin first, then user
    return localStorage.getItem('admin_auth_token') || localStorage.getItem('user_auth_token');
  } catch {
    return null;
  }
};

/**
 * Set authentication via HYBRID approach:
 * 1. Store token in localStorage (primary - always works)
 * 2. Attempt HttpOnly cookie (secondary - additional security)
 */
export async function setAuthCookie(
  token: string, 
  type: 'user' | 'admin', 
  email?: string,
  rememberMe?: boolean
): Promise<boolean> {
  try {
    // Check for rememberMe preference from localStorage if not provided
    let shouldRemember = rememberMe;
    if (shouldRemember === undefined) {
      try {
        const stored = localStorage.getItem('auth_remember_me');
        shouldRemember = stored === 'true';
        // Clean up after reading
        localStorage.removeItem('auth_remember_me');
      } catch {
        shouldRemember = true; // Default to true
      }
    }
    
    authLogger.info('Setting authentication', { type, email, rememberMe: shouldRemember });
    
    // Validate inputs
    if (!token || typeof token !== 'string' || token.length < 10) {
      authLogger.error('Invalid token provided', { tokenLength: token?.length });
      return false;
    }
    
    // STEP 1: Store token in localStorage (primary, always works)
    const prefix = type === 'admin' ? 'admin' : 'user';
    try {
      localStorage.setItem(`${prefix}_auth_token`, token);
      localStorage.setItem(`${prefix}_auth_type`, type);
      if (email) {
        localStorage.setItem(`${prefix}_auth_email`, email);
      }
      // Store remember me preference for session duration
      localStorage.setItem(`${prefix}_remember_me`, shouldRemember ? 'true' : 'false');
      authLogger.tokenStored(type, email || 'unknown');
    } catch (storageError) {
      authLogger.error('localStorage failed', { error: String(storageError) });
      return false;
    }
    
    // STEP 2: Attempt to set HttpOnly cookie (secondary, may fail cross-origin)
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
      
      const response = await fetch(`${SUPABASE_URL}/functions/v1/set-auth-cookie`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ token, type, rememberMe: shouldRemember }),
        credentials: 'include',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        authLogger.debug('HttpOnly cookie also set', { rememberMe: shouldRemember });
      } else {
        authLogger.debug('HttpOnly cookie not set (cross-origin), localStorage active');
      }
    } catch (cookieError) {
      // This is expected for cross-origin requests
      authLogger.debug('HttpOnly cookie skipped (cross-origin)');
    }
    
    authLogger.info('Authentication set successfully', { type, email, rememberMe: shouldRemember });
    return true;
  } catch (error) {
    authLogger.error('Error setting auth', { error: String(error) });
    return false;
  }
}

/**
 * Clear authentication.
 *
 * NOTE: This is a thin wrapper around `destroySession()` (single source of
 * truth in `@/utils/sessionToken`). Both legacy direct callers (apiClient,
 * useTokenValidation) and modern callers funnel through one path so all
 * ancillary keys (user_roles, sirppi_view_mode, fresh_login_grace_at,
 * sirppi_profile_id, sessionStorage flags) are cleared together.
 */
export async function clearAuthCookie(): Promise<boolean> {
  try {
    const { destroySession } = await import('@/utils/sessionToken');
    await destroySession({ revokeServerSide: true });
    authLogger.tokenCleared('clearAuthCookie -> destroySession');
    return true;
  } catch (error) {
    authLogger.error('Error clearing auth', { error: String(error) });
    return false;
  }
}

/**
 * Validate session via edge function with retry logic
 */
export async function getAuthFromCookie(
  context?: 'admin' | 'user'
): Promise<CookieValidationResult | null> {
  const maxRetries = 2;
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await validateSessionInternal(context);
      if (result) return result;
      
      // If result is null but no error, user is not authenticated
      if (attempt === 0) return null;
    } catch (error: any) {
      lastError = error;
      console.warn(`[Auth] Validation attempt ${attempt + 1} failed:`, error.message);
      
      // Wait before retry (exponential backoff)
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
  }
  
  console.error('[Auth] All validation attempts failed:', lastError?.message);
  return null;
}

/**
 * Internal validation function
 */
async function validateSessionInternal(
  context?: 'admin' | 'user'
): Promise<CookieValidationResult | null> {
  // Get localStorage token
  const token = getLocalStorageToken(context);
  
  // If no token, user is not logged in
  if (!token) {
    authLogger.debug('No token found, user not authenticated', { context });
    return null;
  }
  
  authLogger.debug('Validating session with server', { 
    context,
    tokenLength: token.length 
  });
  
  // Create abort controller with timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
  
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/validate-session-cookie`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY
      },
      body: JSON.stringify({ 
        context: context || null,
        sessionToken: token
      }),
      credentials: 'include',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      if (response.status === 401) {
        // Token invalid - clear local storage
        authLogger.warn('Token invalid (401), clearing local storage', { context });
        try {
          const prefix = context === 'admin' ? 'admin' : 'user';
          localStorage.removeItem(`${prefix}_auth_token`);
          localStorage.removeItem(`${prefix}_auth_type`);
          localStorage.removeItem(`${prefix}_auth_email`);
        } catch {}
        return null;
      }
      throw new Error(`Validation request failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data || !data.isValid) {
      authLogger.warn('Session validation failed', { reason: data?.failureReason });
      return null;
    }
    
    authLogger.info('Session validated successfully', { 
      email: data.userEmail, 
      isAdmin: data.isAdmin 
    });
    
    return {
      isValid: data.isValid,
      userEmail: data.userEmail,
      userName: data.userName,
      paymentStatus: data.paymentStatus,
      expiresAt: data.expiresAt ? new Date(data.expiresAt) : null,
      usesRemaining: data.usesRemaining,
      failureReason: data.failureReason,
      isAdmin: data.isAdmin || false,
      profileId: data.profileId || null
    };
  } catch (error: any) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      authLogger.error('Validation request timed out', { context });
      throw new Error('Validation request timed out');
    }
    authLogger.error('Validation request error', { error: error.message, context });
    throw error;
  }
}
